// Orders list filters (owner, 2026-10-21, after the Blanxer layout): the period (Today, Last 7 days…),
// a from–to date range, a Filters popover (order status, payment status, payment method, sales
// channel) and Logistics status. All of them map to the GET …/orders query; nothing is filtered here.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatDate } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";
import {
  LOGISTICS_STATES,
  type LogisticsState,
  type OrderListFilter,
  type OrderStatus,
  type PaymentStatus,
} from "./api";
import { Section } from "./NewOrderDialog";
import { MethodMark, methodLabel, orderStatusLabel, paymentStatusLabel } from "./ui";

export type Period =
  "all" | "today" | "yesterday" | "d7" | "d30" | "month" | "lastMonth" | "custom";
const PERIODS: Period[] = ["today", "yesterday", "d7", "d30", "month", "lastMonth", "all"];

export interface Extra {
  status?: OrderStatus;
  payment_status?: PaymentStatus;
  payment_method?: string;
  channel?: string;
}

const STATUSES: OrderStatus[] = [
  "pending",
  "confirmed",
  "processing",
  "ready",
  "shipped",
  "delivered",
  "completed",
  "cancelled",
  "returned",
];
const PAYMENT_STATUSES: PaymentStatus[] = [
  "unpaid",
  "partial",
  "paid",
  "awaiting_verification",
  "overpaid",
  "refunded",
  "partially_refunded",
];
const METHODS = [
  "cod",
  "manual_qr",
  "esewa",
  "khalti",
  "fonepay_qr",
  "fonepay_web",
  "cash",
  "bank_transfer",
];
const CHANNELS = ["web", "pos", "manual", "marketplace"];

const DAY = 86_400_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
/** yyyy-mm-dd in local time (the value of a date input). */
const isoDay = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fromIsoDay = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y!, m! - 1, d!).getTime();
};

/** The placed_at window for a period: `from` inclusive, `to` exclusive (ms, local days). */
export function periodRange(
  period: Period,
  custom: { from: string; to: string },
  now = Date.now(),
): { from?: number; to?: number } {
  const today = startOfDay(new Date(now));
  switch (period) {
    case "today":
      return { from: today };
    case "yesterday":
      return { from: today - DAY, to: today };
    case "d7":
      return { from: today - 6 * DAY };
    case "d30":
      return { from: today - 29 * DAY };
    case "month": {
      const d = new Date(now);
      return { from: new Date(d.getFullYear(), d.getMonth(), 1).getTime() };
    }
    case "lastMonth": {
      const d = new Date(now);
      return {
        from: new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(),
        to: new Date(d.getFullYear(), d.getMonth(), 1).getTime(),
      };
    }
    case "custom":
      return {
        ...(custom.from ? { from: fromIsoDay(custom.from) } : {}),
        ...(custom.to ? { to: fromIsoDay(custom.to) + DAY } : {}),
      };
    default:
      return {};
  }
}

/** Open state (own, or the page's when it must open the panel itself) + close on outside click / Esc. */
function usePopover(ext?: { open: boolean; setOpen: (o: boolean) => void }) {
  const [own, setOwn] = useState(false);
  const open = ext ? ext.open : own;
  const setOpen = ext ? ext.setOpen : setOwn;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);
  return { open, setOpen, ref };
}

const controlCls =
  "flex min-h-control flex-none items-center gap-2 rounded-xs border border-control bg-surface px-3 text-sm whitespace-nowrap hover:bg-hover";
const panelCls =
  "absolute start-0 top-full z-(--ady-z-popover) mt-1 flex flex-col gap-2.5 rounded-sm border border-subtle bg-overlay p-3 shadow-2";

/** "Last 7 days" and the other quick periods (a native select: keyboard and phones for free). */
export function PeriodSelect({
  value,
  onChange,
  onCustom,
}: {
  value: Period;
  onChange: (p: Period) => void;
  /** "Custom dates": the page opens the date picker. */
  onCustom: () => void;
}) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="relative flex-none" ref={ref}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cx(controlCls, open ? "ady-outline-brand text-link" : "")}
      >
        <span className="ady-text-brand">
          <Icon name="timer" gradient />
        </span>
        {t(`commerce.orders.period.${value}`)}
        <span className="text-fg-tertiary">
          <Icon name="chevronDown" />
        </span>
      </button>
      {open ? (
        <div
          className={cx(panelCls, "w-56")}
          role="dialog"
          aria-label={t("commerce.orders.period.label")}
        >
          <PanelHead
            icon="timer"
            title={t("commerce.orders.period.label")}
            onClose={() => setOpen(false)}
          />
          <ul className="flex flex-col gap-1.5">
            {[...PERIODS, "custom" as const].map((p) => (
              <li key={p}>
                <button
                  type="button"
                  aria-pressed={value === p}
                  onClick={() => {
                    setOpen(false);
                    if (p === "custom") onCustom();
                    else onChange(p);
                  }}
                  className={cx(chipCls(value === p), "w-full justify-between")}
                >
                  {t(`commerce.orders.period.${p}`)}
                  {p === "custom" ? (
                    <Icon name="calendar" />
                  ) : value === p ? (
                    <Icon name="check" />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** A popover head in the site's style: brand tile, title, optional note, close. */
function PanelHead({
  icon,
  title,
  note,
  onClose,
}: {
  icon: IconName;
  title: string;
  note?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-subtle pb-2">
      <span className="flex size-7 flex-none items-center justify-center rounded-xs ady-bg-brand text-on-primary">
        <Icon name={icon} />
      </span>
      <span className="flex-1 text-sm font-semibold">{title}</span>
      {note}
      <button
        type="button"
        onClick={onClose}
        className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
      >
        <Icon name="x" />
        <span className="ady-sr-only">{t("commerce.orders.filters.close")}</span>
      </button>
    </div>
  );
}

const chipCls = (on: boolean) =>
  cx(
    "flex min-h-7 items-center gap-1.5 rounded-full border-[1.5px] px-2.5 text-xs font-medium whitespace-nowrap",
    on
      ? "ady-outline-brand font-semibold text-link"
      : "border-card bg-surface text-fg-secondary hover:bg-hover",
  );

const WEEK_START = 0; // Sunday, as calendars in Nepal start the week

/** One month of days (with leading blanks) for the calendar grid. */
function monthDays(view: Date): (number | null)[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const blanks = (first.getDay() - WEEK_START + 7) % 7;
  const count = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  return [
    ...Array.from({ length: blanks }, () => null),
    ...Array.from({ length: count }, (_, i) =>
      new Date(view.getFullYear(), view.getMonth(), i + 1).getTime(),
    ),
  ];
}

/**
 * The date picker: our own calendar (click the first day, then the last; the range is shaded) and
 * the chosen range with Apply. Quick periods live in the period list beside it. Future days are off.
 */
export function DateRange({
  period,
  custom,
  open: openProp,
  onOpenChange,
  onApply,
  align = "start",
}: {
  period: Period;
  custom: { from: string; to: string };
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onApply: (range: { from: string; to: string } | null) => void;
  /** "end": the panel opens leftwards, for a button at the right edge. */
  align?: "start" | "end";
}) {
  const { open, setOpen, ref } = usePopover({ open: openProp, setOpen: onOpenChange });
  const r = periodRange(period, custom);
  const today = startOfDay(new Date());
  const [start, setStart] = useState<number | null>(null);
  const [end, setEnd] = useState<number | null>(null);
  const [view, setView] = useState(() => new Date());
  useEffect(() => {
    if (!open) return;
    const s = r.from ?? null;
    const e = r.to !== undefined ? r.to - DAY : s !== null ? today : null;
    setStart(s);
    setEnd(e);
    setView(new Date(e ?? today));
  }, [open]);
  const span = (a: number, b: number) => `${formatDate(a)} – ${formatDate(b)}`;
  const label =
    r.from === undefined && r.to === undefined
      ? t("commerce.orders.range.all")
      : span(r.from ?? today, r.to !== undefined ? r.to - DAY : today);
  const pickDay = (d: number) => {
    if (start === null || end !== null) {
      setStart(d);
      setEnd(null);
    } else if (d < start) {
      setEnd(start);
      setStart(d);
    } else setEnd(d);
  };
  const days = start !== null && end !== null ? Math.round((end - start) / DAY) + 1 : 0;
  const shift = (n: number) => setView(new Date(view.getFullYear(), view.getMonth() + n, 1));
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Date(2026, 2, 1 + ((i + WEEK_START) % 7)).toLocaleDateString(undefined, {
      weekday: "narrow",
    }),
  ); // 1 Mar 2026 is a Sunday
  return (
    <div className="relative flex-none" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cx(controlCls, open ? "ady-outline-brand text-link" : "")}
      >
        <span className="ady-text-brand">
          <Icon name="calendar" gradient />
        </span>
        {label}
      </button>
      {open ? (
        <div
          className={cx(panelCls, "w-max", align === "end" && "start-auto end-0")}
          role="dialog"
          aria-label={t("commerce.orders.range.title")}
        >
          <PanelHead
            icon="calendar"
            title={t("commerce.orders.range.title")}
            onClose={() => setOpen(false)}
          />
          <div className="flex justify-center">
            {/* Seven 32px days: the panel is exactly as wide as the calendar (owner, 2026-10-21). */}
            <div className="flex w-56 flex-col gap-1">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => shift(-1)}
                  className="inline-flex size-7 items-center justify-center rounded-full text-fg-secondary hover:bg-hover"
                >
                  <Icon name="chevronLeft" />
                  <span className="ady-sr-only">{t("commerce.orders.range.prev")}</span>
                </button>
                <span className="text-sm font-semibold">
                  {view.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                </span>
                <button
                  type="button"
                  disabled={new Date(view.getFullYear(), view.getMonth() + 1, 1).getTime() > today}
                  onClick={() => shift(1)}
                  className="inline-flex size-7 items-center justify-center rounded-full text-fg-secondary hover:bg-hover disabled:opacity-30"
                >
                  <Icon name="chevronRight" />
                  <span className="ady-sr-only">{t("commerce.orders.range.next")}</span>
                </button>
              </div>
              <div className="grid grid-cols-7 text-center text-2xs font-semibold text-fg-tertiary uppercase">
                {weekdays.map((w, i) => (
                  <span key={i} className="py-1">
                    {w}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-0.5">
                {monthDays(view).map((d, i) => {
                  if (d === null) return <span key={`b${i}`} />;
                  const edge = d === start || d === end;
                  const inside = start !== null && end !== null && d > start && d < end;
                  const future = d > today;
                  return (
                    <span
                      key={d}
                      className={cx(
                        "flex justify-center",
                        inside && "bg-selected",
                        d === start && end !== null && end > start && "rounded-s-full bg-selected",
                        d === end && start !== null && end > start && "rounded-e-full bg-selected",
                      )}
                    >
                      <button
                        type="button"
                        disabled={future}
                        aria-pressed={edge}
                        onClick={() => pickDay(d)}
                        className={cx(
                          "ady-ledger flex size-8 items-center justify-center rounded-full text-xs",
                          edge
                            ? "ady-bg-brand font-semibold text-on-primary"
                            : inside
                              ? "font-medium text-link hover:bg-hover"
                              : "hover:bg-hover",
                          d === today && !edge && "font-bold text-link",
                          future && "cursor-not-allowed opacity-30",
                        )}
                      >
                        {new Date(d).getDate()}
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex w-56 flex-col gap-1.5 self-center border-t border-subtle pt-2">
            <span className="text-xs text-fg-secondary">
              {start !== null ? (
                <>
                  <span className="font-semibold text-fg">{span(start, end ?? start)}</span>
                  {" · "}
                  {t("commerce.orders.range.days", { count: days || 1 })}
                </>
              ) : (
                t("commerce.orders.range.hint")
              )}
            </span>
            <span className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  onApply(null);
                  setOpen(false);
                }}
                className="min-h-control text-sm text-fg-secondary hover:text-fg"
              >
                {t("commerce.orders.range.clear")}
              </button>
              <button
                type="button"
                disabled={start === null}
                onClick={() => {
                  if (start === null) return;
                  onApply({ from: isoDay(start), to: isoDay(end ?? start) });
                  setOpen(false);
                }}
                className="flex min-h-control items-center gap-1.5 rounded-xs ady-bg-brand px-3.5 text-sm font-medium text-on-primary disabled:opacity-50"
              >
                <Icon name="check" />
                {t("commerce.orders.range.apply")}
              </button>
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FilterGroup<T extends string>({
  icon,
  title,
  options,
  value,
  label,
  mark,
  onChange,
}: {
  icon: IconName;
  title: string;
  options: readonly T[];
  value: T | undefined;
  label: (v: T) => string;
  mark?: (v: T) => ReactNode;
  onChange: (v: T | undefined) => void;
}) {
  return (
    <Section icon={icon} title={title}>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            aria-pressed={value === o}
            onClick={() => onChange(value === o ? undefined : o)}
            className={chipCls(value === o)}
          >
            {mark?.(o)}
            {label(o)}
          </button>
        ))}
      </div>
    </Section>
  );
}

/** "Filters": order status, payment status, payment method and sales channel as chips. */
export function FiltersButton({ value, onChange }: { value: Extra; onChange: (v: Extra) => void }) {
  const { open, setOpen, ref } = usePopover();
  const count = Object.values(value).filter(Boolean).length;
  const set = <K extends keyof Extra>(k: K, v: Extra[K]) => onChange({ ...value, [k]: v });
  return (
    <div className="relative flex-none" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cx(controlCls, open || count ? "ady-outline-brand text-link" : "")}
      >
        <span className="ady-text-brand">
          <Icon name="sliders" gradient />
        </span>
        {t("commerce.orders.filters.title")}
        {count ? (
          <span className="ady-ledger flex size-5 items-center justify-center rounded-full ady-bg-brand text-2xs font-semibold text-on-primary">
            {count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className={cx(panelCls, "w-96 max-w-[calc(100vw-2rem)]")}
          role="dialog"
          aria-label={t("commerce.orders.filters.title")}
        >
          <PanelHead
            icon="sliders"
            title={t("commerce.orders.filters.title")}
            note={
              count ? (
                <span className="text-xs text-fg-secondary">
                  {t("commerce.orders.filters.active", { count })}
                </span>
              ) : undefined
            }
            onClose={() => setOpen(false)}
          />
          <FilterGroup
            icon="orders"
            title={t("commerce.orders.filters.status")}
            options={STATUSES}
            value={value.status}
            label={orderStatusLabel}
            onChange={(v) => set("status", v)}
          />
          <FilterGroup
            icon="money"
            title={t("commerce.orders.filters.payment")}
            options={PAYMENT_STATUSES}
            value={value.payment_status}
            label={paymentStatusLabel}
            onChange={(v) => set("payment_status", v)}
          />
          <FilterGroup
            icon="card"
            title={t("commerce.orders.filters.method")}
            options={METHODS}
            value={value.payment_method}
            label={methodLabel}
            mark={(m) => <MethodMark method={m} />}
            onChange={(v) => set("payment_method", v)}
          />
          <FilterGroup
            icon="store"
            title={t("commerce.orders.filters.channel")}
            options={CHANNELS}
            value={value.channel}
            label={(c) =>
              i18n.tDynamic(`commerce.orders.channel.${c}`, "commerce.orders.channel.manual")
            }
            onChange={(v) => set("channel", v)}
          />
          <div className="flex items-center justify-between gap-2 border-t border-subtle pt-2.5">
            <button
              type="button"
              disabled={!count}
              onClick={() => onChange({})}
              className="flex min-h-control items-center gap-1.5 px-1 text-sm text-fg-secondary hover:text-fg disabled:opacity-40"
            >
              <Icon name="x" />
              {t("commerce.orders.filters.clear")}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex min-h-control items-center gap-1.5 rounded-xs ady-bg-brand px-3.5 text-sm font-medium text-on-primary"
            >
              <Icon name="check" />
              {t("commerce.orders.filters.done")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** "Logistics status": orders by where their parcel is, from the courier check. */
export function LogisticsFilter({
  value,
  onChange,
}: {
  value: LogisticsState | null;
  onChange: (v: LogisticsState | null) => void;
}) {
  const { open, setOpen, ref } = usePopover();
  const pick = (v: LogisticsState | null) => {
    onChange(v);
    setOpen(false);
  };
  return (
    <div className="relative flex-none" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cx(
          "flex min-h-8 items-center gap-1.5 rounded-full border px-3.5 text-xs font-medium whitespace-nowrap",
          value
            ? "border-transparent ady-bg-brand text-on-primary"
            : "border-card bg-surface text-fg hover:bg-hover",
        )}
      >
        <Icon name="truck" />
        {value
          ? t(`commerce.orders.logistics.states.${value}`)
          : t("commerce.orders.logistics.title")}
        <Icon name="chevronDown" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute start-0 top-full z-(--ady-z-popover) mt-1 flex min-w-52 flex-col rounded-sm border border-subtle bg-overlay py-1 shadow-2"
        >
          <span className="px-3 py-1 text-2xs font-semibold text-fg-tertiary uppercase">
            {t("commerce.orders.logistics.title")}
          </span>
          {LOGISTICS_STATES.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={value === s}
              onClick={() => pick(value === s ? null : s)}
              className={cx(
                "flex min-h-control items-center justify-between gap-2 px-3 text-start text-sm hover:bg-hover",
                value === s ? "font-semibold text-link" : "text-fg",
              )}
            >
              {t(`commerce.orders.logistics.states.${s}`)}
              {value === s ? <Icon name="check" /> : null}
            </button>
          ))}
          {value ? (
            <button
              type="button"
              onClick={() => pick(null)}
              className="mt-1 flex min-h-control items-center gap-2 border-t border-subtle px-3 text-start text-sm text-fg-secondary hover:bg-hover"
            >
              <Icon name="x" />
              {t("commerce.orders.logistics.clear")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The list filter the page sends (and Export to Excel reuses). */
export const listFilter = (
  base: Pick<OrderListFilter, "view" | "q" | "logistics">,
  period: Period,
  custom: { from: string; to: string },
  extra: Extra,
): OrderListFilter => ({ ...base, ...periodRange(period, custom), ...extra });
